import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import RTSDataFilter from "./RTSDataFilter";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { ReturnToStationResponse } from "../../app/models/responseModels/returnToStationResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import { PACKAGE_STATUS_ARRAY } from "../helper/helper";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import {
  downloadReturnToStationDataToCsv,
  downloadReturnToStationDataToXlsx,
  getReturnToStationList,
  processBarcodeForRTS,
} from "../../app/features/returnToStation/returnToStationSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import { BarcodeReceivingModel } from "../../app/models/requestModels/processBarcode";
import useScanDetection from "../receiving/useScanDetection";
import { ParcelCenterResponse } from "../../app/models/responseModels/parcelCenterResponse";
import ViewRTSModal from "./modals/ViewRTSModal";
import { useSubmitModalWithParameter } from "../../components/common/modalPopup/useSubmitModal";
import {
  FDC_Message,
  beepFail,
  beepPass,
} from "../../functions/fdcMessage/FdcMessage";

const breadcrumb = ["Return To Station"];

const ReturnToStation = () => {
  const dispatch = useAppDispatch();
  const { isLoading, returnToStationList } = useSelector(
    (state: RootState) => state.returnToStation
  );

  // ***** Check user has admin role *****
  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.roles.find(
      (x) => x === "Administrators"
    )
  );

  // ***** get parcel center assigned to user *****
  const assignedParcelCenters = useSelector(
    (state: RootState) =>
      state.login?.authResponse?.userSettings?.assignedParcelCenters
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [filteredData, setFilteredData] = useState<ReturnToStationResponse[]>(
    []
  );
  const [barcode, setBarcode] = useState<String | null>("");

  const [scanParcelCenter, setscanParcelCenter] = useState("");
  const [scanParcelCenterOptions, setscanParcelCenterOptions] = useState<
    ParcelCenterResponse[]
  >([]);

  // ***** Callbacks *****
  const getReturnToStationData = useCallback(async () => {
    try {
      const response = await dispatch(getReturnToStationList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "returnToStationList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "returnToStationListException",
      });
    }
  }, [dispatch]);

  const processScanRequest = useCallback(
    async (barcode: String) => {
      try {
        const barcodeDetails = BarcodeReceivingModel(barcode, scanParcelCenter);
        setBarcode(null);
        if (barcodeDetails.barcode.toLocaleLowerCase().includes("control")) {
          return;
        }
        if (barcodeDetails.barcode.length < 6) {
          FDC_Message("red", "Invalid barcode", 3000);
          beepFail();
          return;
        }
        if (barcodeDetails.parcelCenterId === "") {
          toast.error("Please select parcel center before scanning");
          return;
        }
        const response = await dispatch(
          processBarcodeForRTS(barcodeDetails)
        ).unwrap();
        if (!response.isSuccess) {
          FDC_Message("red", response.message, 3000);
          beepFail();
        } else {
          FDC_Message("springgreen", response.message, 3000);
          beepPass();
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: `processRTSScanRequestException${barcode}`,
        });
      }
    },
    [dispatch, scanParcelCenter]
  );

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getReturnToStationData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getReturnToStationData]);

  useEffect(() => {
    if (returnToStationList.length > 0) {
      if (filteredData.length === returnToStationList.length) {
        setFilteredData(returnToStationList);
      }
    } else {
      setFilteredData([]);
    }
  }, [returnToStationList, filteredData]);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan && barcode) {
      processScanRequest(barcode);
    }
    return () => {
      isFunctionRan = false;
    };
  }, [barcode, processScanRequest]);

  useEffect(() => {
    if (assignedParcelCenters) {
      setscanParcelCenterOptions(assignedParcelCenters);
      if (assignedParcelCenters.length === 1) {
        setscanParcelCenter(assignedParcelCenters[0].parcelCenterId);
      }
    }
  }, [dispatch, assignedParcelCenters]);

  // ***** Scan Detection *****
  useScanDetection({
    onComplete: (code) => {
      setBarcode(code);
    },
    minLength: 3,
  });

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<ReturnToStationResponse>[]>(
    () => [
      {
        accessorKey: "parcelCenterLocationName",
        header: "Parcel Center",
        size: 150,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: "shipmentClientName",
        header: "Client",
        size: 150,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: "trackingNumber",
        header: "Tracking Number",
        size: 200,
      },
      {
        accessorKey: "status",
        header: "Package Status",
        size: 150,
        filterVariant: "multi-select",
        filterSelectOptions: PACKAGE_STATUS_ARRAY,
      },
      {
        accessorKey: "statusDate",
        header: "Status Date",
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <span>{new Date(cell.getValue<string>()).toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: "deliveryDate",
        header: "Last Scheduled Delivery",
        size: 200,
      },
      {
        accessorKey: "shipmentRecipientCity",
        header: "Recipient City",
        size: 150,
      },
      {
        accessorKey: "shipmentRecipientState",
        header: "Recipient State",
        size: 150,
      },
      {
        accessorKey: "shipmentRecipientZip",
        header: "Recipient Zip",
        size: 150,
      },
      {
        accessorKey: "updatedOn",
        header: "Updated On",
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <span>{new Date(cell.getValue<string>()).toLocaleString()}</span>
          </div>
        ),
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getReturnToStationData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadReturnToStationDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "returnToStationDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "returnToStationDownloadCsvException",
      });
    }
    handleClose();
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadReturnToStationDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "ReturnToStation.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "returnToStationDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "returnToStationDownloadXlsxException",
      });
    }
  };

  const handleScanParcelCenterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let selectedOption = event.target.value as string;
    setscanParcelCenter(selectedOption);
  };

  // ***** Modal Popups *****
  const [viewRTSModal, openViewRTSModal] =
    useSubmitModalWithParameter<ReturnToStationResponse>(
      (returnToStationRowData, closeViewRTSModal) => (
        <ViewRTSModal
          closeViewRTSModal={closeViewRTSModal}
          onCompleted={getReturnToStationData}
          returnToStationRowData={returnToStationRowData}
        />
      )
    );

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<ReturnToStationResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        <TextField
          select
          key="userParcelCenter"
          margin="none"
          id="userParcelCenter"
          label="Parcel Center"
          name="userParcelCenter"
          size="small"
          value={scanParcelCenter}
          onChange={handleScanParcelCenterChange}
          sx={{ minWidth: 125, ml: 1 }}
        >
          {scanParcelCenterOptions.map((Option, index) => (
            <MenuItem value={Option.parcelCenterId} key={Option.parcelCenterId}>
              {Option.parcelCenterName}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      {IsAdmin && (
        <>
          <RTSDataFilter
            returnToStationData={returnToStationList}
            setFilteredData={setFilteredData}
            filteredData={filteredData}
          />
          <Divider />
          <br />
        </>
      )}
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No returnToStation packages"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="updatedOn"
        openModal={openViewRTSModal}
      />
      {viewRTSModal}
    </Box>
  );
};

export default ReturnToStation;
